import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
  Checkbox,
  fade,
} from "@material-ui/core";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PrintIcon from "@material-ui/icons/Print";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import { Icon } from "@iconify/react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";

import { useRouteMatch } from "react-router-dom";
import { CustomTheme } from "../../../../../../types/customTheme";
import { updateWrapperInfo } from "../../../../../../services/menuApp/menuService";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_UPDATED,
} from "../../../../../../utils/consts";
import { handleImageUrl } from "../../../../../../utils/ChangePixelImgUrl";
import { getLocalStore } from "../../../../../../utils/store/localStore";
import { AxiosError } from "axios";
import { updateDirectoryInStore } from "../../../../../../redux/actions/menu";

interface WrapperNodeProps {
  wrapper: any;
  category: any;
  isGridView: boolean;
  selectedIds?: string[];
  handleItemSelect?: (id: string) => void;
  tags?: any;
  menuNodeList: any;
  setMenuNodeList: any;
  menuId: string;
  categoryId: string;
  productVersion: any;
  locationId: string;
  onDirectoryVisibilityUpdated?: () => void;
  forceUpdateKey?: any;
  handleSelectAll?: (isChecked: boolean, wrapper: any) => void;
  handleOpenProductEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenMappingModal?: (
    event: any,
    category: any,
    item: any,
    type: string,
    isMap?: any,
  ) => void;
  handleOpenDirectoryEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenProductOrDirectoryModals?: (category: any, wrapper?: any) => void;
  setIsProductAdd?: any;
  setError?: any;
  onDirectoryNavigate?: (directory: any, category: any, menuItem: any) => void;
  onProductNavigate?: (
    product: any,
    wrapper: any,
    category: any,
    menuItem: any,
  ) => void;
  currentCategory?: any;
  currentMenuItem?: any;
  isOpenWrapperEditModal?: any;
  isRoot?: any;
  firstWrapperId?: any;
  wrapperId?: any;
  removedLinkedIdLists?: any;
  handleClickLinkedId?: any;
  handleClearStrip?: any;
  handleNavigate?: any;
  isEditDisabled: boolean;
}

const WrapperNode: React.FC<WrapperNodeProps> = ({
  wrapper,
  category,
  isGridView,
  selectedIds,
  handleItemSelect,
  tags,
  menuNodeList,
  setMenuNodeList,
  menuId,
  categoryId,
  productVersion,
  locationId,
  onDirectoryVisibilityUpdated,
  forceUpdateKey,
  handleOpenProductEditModal,
  handleOpenMappingModal,
  handleOpenDirectoryEditModal,
  handleOpenProductOrDirectoryModals,
  setIsProductAdd,
  setError,
  onDirectoryNavigate,
  onProductNavigate,
  currentCategory,
  currentMenuItem,
  isOpenWrapperEditModal,
  isRoot,
  firstWrapperId,
  wrapperId,
  removedLinkedIdLists,
  handleClickLinkedId,
  handleClearStrip,
  handleNavigate,
  isEditDisabled,
}) => {
  const theme: CustomTheme = useTheme();
  const match: any = useRouteMatch();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [localSuccess, setLocalSuccess] = useState<string>("");

  // SAFE CHECK: Ensure wrapper exists
  if (!wrapper) {
    return null;
  }

  // SAFE CHECK: Check if directory property exists with fallback
  const isDirectory =
    wrapper.directory === true || wrapper.directory === "true";

  // Improved getFormattedPrice function with better formatting
  const getFormattedPrice = () => {
    if (isDirectory) return null;

    // Try to get price from wrapper (now includes price from API)
    let priceValue = wrapper?.price;

    // If price is not in wrapper, try to get from menuNodeList
    if (!priceValue || priceValue === "0") {
      if (
        menuNodeList[menuId]?.category[categoryId]?.wrapper?.[wrapper.id]?.price
      ) {
        priceValue =
          menuNodeList[menuId].category[categoryId].wrapper[wrapper.id].price;
      }
    }

    // Handle null, undefined, or empty values
    if (priceValue === null || priceValue === undefined || priceValue === "") {
      return "0.00";
    }

    // Handle zero values
    if (priceValue === "0" || priceValue === "0.00" || priceValue === 0) {
      return "0.00";
    }

    // Convert to number and format to 2 decimal places
    const numericPrice = parseFloat(priceValue);
    if (isNaN(numericPrice)) {
      return "0.00";
    }

    return numericPrice.toFixed(2);
  };

  const findAndUpdateDirectory = (
    node: any,
    targetId: number,
    updatedData: any,
  ): boolean => {
    if (!node) return false;
    if (node.id === targetId) {
      Object.assign(node, updatedData);
      return true;
    }
    if (node.wrapper && typeof node.wrapper === "object") {
      for (const key in node.wrapper) {
        if (
          node.wrapper[key] &&
          findAndUpdateDirectory(node.wrapper[key], targetId, updatedData)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const handleChangeValidInWrapper = async (
    itemData: any,
    e?: React.MouseEvent,
  ) => {
    if (e) {
      e.stopPropagation();
    }

    // SAFE CHECK: Ensure itemData exists
    if (!itemData || !itemData.id) {
      console.error("Invalid item data for visibility change", itemData);
      if (setError) {
        setError("Unable to update item status");
      }
      return;
    }

    setIsLoading(true);

    const body = {
      locationId: match.params.locationId,
      valid: !itemData.valid,
      id: itemData.id,
      version: itemData.version,
    };

    try {
      const res = await updateWrapperInfo(
        match.params.locationId,
        body,
        "Menus Changed",
        "",
        "menu",
      );

      if (res.data && (res.status === 200 || res.status === 201)) {
        const updatedItemFromResponse = res.data.data;

        // Deep clone the menu node list
        const updatedList = _.cloneDeep(menuNodeList);

        // Try to find and update the item in the structure
        let updated = false;

        // Check if the menu exists
        if (updatedList[menuId]?.category[categoryId]?.wrapper) {
          const categoryWrapper =
            updatedList[menuId].category[categoryId].wrapper;

          // Direct match
          if (categoryWrapper[itemData.id]) {
            // Preserve existing properties while updating with new version
            categoryWrapper[itemData.id] = {
              ...categoryWrapper[itemData.id],
              ...updatedItemFromResponse,
              version: updatedItemFromResponse.version,
            };
            updated = true;
          } else {
            // Search in nested wrappers
            for (const key in categoryWrapper) {
              if (
                categoryWrapper[key] &&
                findAndUpdateDirectory(
                  categoryWrapper[key],
                  itemData.id,
                  updatedItemFromResponse,
                )
              ) {
                updated = true;
                break;
              }
            }
          }
        }

        if (updated) {
          setMenuNodeList(updatedList);
          setLocalSuccess(SUCCESSFULLY_UPDATED);

          // Update Redux store directly with the response data
          const updatedDataForRedux = {
            id: updatedItemFromResponse.id,
            valid: updatedItemFromResponse.valid,
            version: updatedItemFromResponse.version,
            name: updatedItemFromResponse.name,
            colour: updatedItemFromResponse.colour,
            img: updatedItemFromResponse.img,
            desc: updatedItemFromResponse.desc,
            directory: updatedItemFromResponse.directory,
            directoryId: updatedItemFromResponse.directoryId,
            directoryLevel: updatedItemFromResponse.directoryLevel,
            itemType: updatedItemFromResponse.itemType,
            allYouCanEat: updatedItemFromResponse.allYouCanEat,
            kiosk: updatedItemFromResponse.kiosk,
            cover: updatedItemFromResponse.cover,
            nameShort: updatedItemFromResponse.nameShort,
            viewOrder: updatedItemFromResponse.viewOrder,
          };

          dispatch(updateDirectoryInStore(updatedDataForRedux));

          if (onDirectoryVisibilityUpdated) {
            onDirectoryVisibilityUpdated();
          }
          // Clear success message after 2 seconds
          setTimeout(() => setLocalSuccess(""), 2000);
        } else {
          console.warn("Item not found in structure after update", itemData.id);
          if (onDirectoryVisibilityUpdated) {
            onDirectoryVisibilityUpdated();
          }
        }
      }
    } catch (error) {
      const err: any = error as AxiosError;
      console.error("Error updating visibility:", err);

      // Handle version conflict
      if (err?.response?.status === 409) {
        if (setError) {
          setError("Version conflict. Please refresh and try again.");
        }
        // Trigger refresh
        if (onDirectoryVisibilityUpdated) {
          onDirectoryVisibilityUpdated();
        }
      } else if (setError) {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest(".action-button") ||
      target.closest(".add-sub-wrapper")
    ) {
      return;
    }

    if (isDirectory && onDirectoryNavigate) {
      onDirectoryNavigate(
        wrapper,
        currentCategory || category,
        currentMenuItem,
      );
    } else if (!isDirectory && onProductNavigate) {
      onProductNavigate(
        wrapper,
        wrapper,
        currentCategory || category,
        currentMenuItem,
      );
    } else if (!isDirectory && handleOpenProductEditModal) {
      handleOpenProductEditModal(
        wrapper,
        wrapper,
        category,
        isOpenWrapperEditModal,
        isRoot,
      );
    } else if (isDirectory && handleOpenDirectoryEditModal) {
      handleOpenDirectoryEditModal(
        wrapper,
        wrapper,
        category,
        isOpenWrapperEditModal,
        isRoot,
      );
    }
  };

  const handleActionClick = (
    e: React.MouseEvent,
    action: string,
    ...args: any[]
  ) => {
    e.stopPropagation();
    if (action === "edit") {
      if (isDirectory && handleOpenDirectoryEditModal) {
        handleOpenDirectoryEditModal(
          wrapper,
          wrapper,
          category,
          isOpenWrapperEditModal,
          isRoot,
        );
      } else if (!isDirectory && handleOpenProductEditModal) {
        handleOpenProductEditModal(
          wrapper,
          wrapper,
          category,
          isOpenWrapperEditModal,
          isRoot,
        );
      }
    } else if (action === "print") {
      console.log("Open mapping modal", category, wrapper, "item");
      if (handleOpenMappingModal) {
        handleOpenMappingModal(e, category, wrapper, "item");
      }
    } else if (action === "visibility") {
      if (isEditDisabled) return;
      handleChangeValidInWrapper(wrapper, e);
    }
  };

  const handleAddSubWrapper = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (handleOpenProductOrDirectoryModals) {
      handleOpenProductOrDirectoryModals(category, wrapper);
      if (setIsProductAdd) {
        setIsProductAdd(true);
      }
    }
  };

  const isMobile = window.innerWidth <= 600;
  const formattedPrice = getFormattedPrice();
  const currencySymbol = getLocalStore("currency") || "$";

  // GRID VIEW
  if (isGridView || isMobile) {
    return (
      <Grid item md={6} xs={12} lg={4} style={{ paddingTop: "22px" }}>
        <div style={{ overflow: "hidden", zIndex: 0, position: "relative" }}>
          <Card
            style={{
              overflowY: "hidden",
              cursor: "pointer",
              borderRadius: "10px",
              boxShadow: "none",
              backgroundColor: theme.palette.background.entity_background,
              border: `1px solid ${theme.palette.background.entity_border}`,
              position: "relative",
              zIndex: 0,
            }}
            onClick={handleCardClick}
          >
            <Grid container>
              <Grid item xs={12} style={{ display: "flex" }}>
                {wrapper?.img ? (
                  <Box
                    style={{
                      margin: "7px",
                      marginLeft: "10px",
                      borderRadius: "10px",
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                    }}
                  >
                    <div style={{ paddingTop: "12px" }}>
                      <Box
                        style={{
                          backgroundColor:
                            wrapper.colour || theme.palette.primary.main,
                          width: "4px",
                          height: "112px",
                          borderRadius: "10px",
                          marginRight: "12px",
                          flexShrink: 0,
                        }}
                      ></Box>
                    </div>
                    <img
                      src={handleImageUrl(wrapper.img)}
                      alt="not available images"
                      style={{
                        objectFit: "cover",
                        width: "114px",
                        height: "114px",
                        borderRadius: "10px",
                      }}
                    />
                  </Box>
                ) : (
                  <div style={{ display: "flex" }}>
                    <div style={{ paddingTop: "12px", marginLeft: "10px" }}>
                      <Box
                        style={{
                          backgroundColor:
                            wrapper.colour || theme.palette.primary.main,
                          width: "4px",
                          height: "112px",
                          borderRadius: "10px",
                          marginRight: "12px",
                          flexShrink: 0,
                        }}
                      ></Box>
                    </div>
                    <Box
                      style={{
                        marginRight: "7px",
                        marginTop: "10px",
                        borderRadius: "10px",
                        marginBottom: "10px",
                        height: "114px",
                        width: "114px",
                        backgroundColor: theme.palette.background.imgBackground,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isDirectory ? (
                        <Icon
                          icon="mdi:folder-outline"
                          width={52}
                          height={52}
                        />
                      ) : (
                        <ImageOutlinedIcon style={{ fontSize: "52px" }} />
                      )}
                    </Box>
                  </div>
                )}
                <Box
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: "8px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                  }}
                >
                  <Box>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        style={{
                          fontWeight: "bold",
                          color: wrapper?.valid === false ? "white" : "inherit",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        align="left"
                      >
                        {wrapper?.name || "Unnamed"}
                      </Typography>
                      <Chip
                        size="small"
                        label={wrapper?.valid ? "Available" : "Unavailable"}
                        style={{
                          borderRadius: "10px",
                          backgroundColor: wrapper?.valid
                            ? theme.palette.custom.green.main
                            : theme.palette.custom.red.main,
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "10px",
                          height: "18px",
                          marginRight: "8px",
                        }}
                      />
                    </div>
                    {isDirectory && (
                      <div
                        style={{
                          backgroundColor: fade(
                            theme.palette.custom.yellow.main || "",
                            0.9,
                          ),
                          color: "white",
                          fontSize: "10px",
                          padding: "5px 0px 0px 1px",
                          borderRadius: "10px",
                          fontWeight: "bold",
                          zIndex: 10,
                          width: "32px",
                          marginTop: "4px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{ fontWeight: "bold" }}
                        >
                          <FolderOutlinedIcon
                            style={{ fontSize: "18px", color: "white" }}
                          />
                        </Typography>
                      </div>
                    )}
                    <Typography
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textTransform: "lowercase",
                        color: wrapper?.valid === false ? "white" : "inherit",
                        marginTop: "4px",
                      }}
                      variant="body2"
                      align="left"
                    >
                      {wrapper?.desc || ""}
                    </Typography>
                  </Box>
                  {!isDirectory && (
                    <Box style={{ marginTop: "auto" }}>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          marginTop: "8px",
                          marginBottom: "4px",
                          color: theme.palette.custom.orange.contrastText,
                        }}
                        align="left"
                      >
                        {currencySymbol}
                        {formattedPrice}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Actions Container */}
          <div
            style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              display: "flex",
              gap: "4px",
              zIndex: 5,
            }}
          >
            {isDirectory && (
              <Button
                size="small"
                className="action-button"
                style={{
                  minWidth: "28px",
                  height: "28px",
                  backgroundColor: theme.palette.custom.yellow.main,
                  color: theme.palette.custom.yellow.contrastText,
                  borderRadius: "6px",
                  border: "none",
                }}
                onClick={(e) => handleActionClick(e, "edit")}
              >
                <EditIcon style={{ fontSize: "14px" }} />
              </Button>
            )}
            {!isDirectory && (
              <Button
                size="small"
                className="action-button"
                style={{
                  minWidth: "28px",
                  height: "28px",
                  backgroundColor: theme.palette.custom.orange.main,
                  color: theme.palette.custom.orange.contrastText,
                  borderRadius: "6px",
                  border: "none",
                }}
                onClick={(e) => handleActionClick(e, "print")}
              >
                <PrintIcon style={{ fontSize: "14px" }} />
              </Button>
            )}
            <Button
              size="small"
              className="action-button"
              style={{
                minWidth: "28px",
                height: "28px",
                backgroundColor: wrapper?.valid
                  ? theme.palette.custom.green.main
                  : theme.palette.custom.red.main,
                color: "#ffffff",
                borderRadius: "6px",
                border: "none",
              }}
              disabled={isLoading || isEditDisabled}
              onClick={(e) => handleActionClick(e, "visibility")}
            >
              {isLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : wrapper?.valid ? (
                <VisibilityIcon style={{ fontSize: "14px" }} />
              ) : (
                <VisibilityOffIcon style={{ fontSize: "14px" }} />
              )}
            </Button>
          </div>
        </div>
      </Grid>
    );
  }
  // LIST VIEW
  return (
    <Grid item xs={12} style={{ paddingTop: "0px" }}>
      <Card
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          boxShadow: "none",
          border: `1px solid ${theme.palette.background.entity_border}`,
          backgroundColor: theme.palette.background.entity_background,
          minHeight: "70px",
          borderRadius: "0px",
          borderTop: "none",
          cursor: isEditDisabled ? "default" : "pointer",
        }}
        onClick={handleCardClick}
      >
        <Box
          style={{ width: "40px", display: "flex", justifyContent: "center" }}
        >
          <Checkbox
            size="small"
            color="secondary"
            checked={selectedIds?.includes(wrapper?.id?.toString())}
            onChange={() =>
              handleItemSelect &&
              wrapper?.id &&
              handleItemSelect(wrapper.id.toString())
            }
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
        <Box
          style={{
            flex: 5,
            marginLeft: "10px",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            style={{
              backgroundColor: wrapper?.colour || theme.palette.primary.main,
              width: "4px",
              height: "40px",
              borderRadius: "10px",
              marginRight: "12px",
              flexShrink: 0,
            }}
          />
          {wrapper?.img ? (
            <img
              src={handleImageUrl(wrapper.img)}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "8px",
                objectFit: "cover",
                marginRight: "16px",
                flexShrink: 0,
              }}
              alt="thumb"
            />
          ) : (
            <Box
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "8px",
                backgroundColor: theme.palette.background.imgBackground,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "16px",
                flexShrink: 0,
              }}
            >
              {isDirectory ? (
                <Icon icon="mdi:folder-outline" width={24} height={24} />
              ) : (
                <ImageOutlinedIcon fontSize="small" />
              )}
            </Box>
          )}
          <Box style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex" }}>
              <Typography
                style={{
                  fontWeight: "bold",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
                align="left"
              >
                {wrapper?.name || "Unnamed"}
              </Typography>
              {isDirectory && (
                <div
                  style={{
                    marginLeft: "12px",
                    backgroundColor: fade(
                      theme.palette.custom.yellow.main || "",
                      0.9,
                    ),
                    color: "white",
                    fontSize: "10px",
                    padding: "4px",
                    borderRadius: "10px",
                    fontWeight: "bold",
                    marginTop: "4px",
                    height: "27px",
                  }}
                >
                  <FolderOutlinedIcon
                    style={{ fontSize: "18px", color: "white" }}
                  />
                </div>
              )}
            </div>
            <Typography
              variant="caption"
              style={{ opacity: 0.6, display: "block" }}
              align="left"
            >
              {wrapper?.desc || ""}
            </Typography>
          </Box>
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          {!isDirectory && (
            <Typography
              style={{ fontSize: "12px", fontWeight: "bold" }}
              align="left"
            >
              {currencySymbol}
              {formattedPrice}
            </Typography>
          )}
        </Box>
        <Box
          style={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            justifyContent: "start",
          }}
        >
          <Chip
            size="small"
            label={wrapper?.valid ? "Available" : "Unavailable"}
            style={{
              backgroundColor: wrapper?.valid
                ? theme.palette.custom.green.main
                : theme.palette.custom.red.main,
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "11px",
              marginRight: "8px",
            }}
          />
        </Box>
        <Box
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            justifyContent: "flex-end",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isDirectory && (
              <Button
                className="add-sub-wrapper action-button"
                onClick={handleAddSubWrapper}
                style={{
                  minWidth: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  textTransform: "none",
                  border: "none",
                  backgroundColor: theme.palette.custom.blue.main,
                  color: theme.palette.custom.blue.contrastText,
                }}
                disabled={isEditDisabled}
              >
                <AddIcon style={{ fontSize: "16px" }} />
              </Button>
            )}
            {!isDirectory && (
              <Button
                className="action-button"
                onClick={(e) => handleActionClick(e, "print")}
                style={{
                  minWidth: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  textTransform: "none",
                  border: "none",
                  backgroundColor: theme.palette.custom.orange.main,
                  color: theme.palette.custom.orange.contrastText,
                }}
              >
                <PrintIcon style={{ fontSize: "16px" }} />
              </Button>
            )}
            <Button
              className="action-button"
              onClick={(e) => handleActionClick(e, "visibility")}
              disabled={isEditDisabled || isLoading || isEditDisabled}
              style={{
                minWidth: "32px",
                height: "32px",
                borderRadius: "6px",
                textTransform: "none",
                border: "none",
                backgroundColor: wrapper?.valid
                  ? theme.palette.custom.green.main
                  : theme.palette.custom.red.main,
                color: "#ffffff",
                cursor: isEditDisabled ? "default" : "pointer",
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : wrapper?.valid ? (
                <VisibilityIcon style={{ fontSize: "16px" }} />
              ) : (
                <VisibilityOffIcon style={{ fontSize: "16px" }} />
              )}
            </Button>
          </div>
        </Box>
      </Card>
    </Grid>
  );
};

export default WrapperNode;
